// Het puntpaneel (Verkennen ronde 4, G4/A5): één punt, zijn buren per soort
// met de telling vooraf, en zijn bronnen.
//
// Vaste orde: breder (boven), vraagt om / gevraagd door, smaller (onder),
// overlapt (AI-oordeel), verwant (AI-vermoeden). Tien per soort op sterkte,
// uitklappen op verzoek; buren buiten de afbakening grijs met telling. Elke
// buur heeft "waarom" (het verband) en elke bron een klik naar de alinea.

import { useState } from "react";

import { BetaalmuurPil, WaaropPil, WiePil, bewijsTitel } from "./Bewijs";
import { PanelHeader, SectionLabel } from "./ui";
import type { GraafBronnenResponse, GraafPunt } from "../contracts";
import { BUUR_LABEL, bandNaam, zekerheidBand, type Beweging, type Buur, type BuurSoort } from "../lib/verkennen";
import { color, radius, size, weight } from "../tokens";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";

const UITGEVER_LABEL: Record<string, string> = {
  beleid: "beleid en toezicht", onderzoek: "onderzoek", arbeidsmarktdata: "arbeidsmarktdata",
  bedrijf: "bedrijfsleven", media: "vakmedia", onderwijs: "onderwijs", regionaal: "regionaal", gemengd: "gemengd",
};
const SOORT_KLEUR: Record<BuurSoort, string> = {
  boven: "#f59e0b", onder: "#f59e0b", vraagt: "#0d9488", gevraagd: "#0d9488", overlapt: "#f59e0b", verwant: "#94a3b8",
};

export function PuntPaneel({
  punt,
  buren,
  puntVan,
  beweging,
  onderwerpen,
  bronnen,
  vlaggen,
  onClose,
  onKies,
  onVerband,
  onOpenAlinea,
  onFocus,
  focusAan,
  onVraag,
  onOpenTrend,
}: {
  punt: GraafPunt;
  buren: Buur[];
  puntVan: (i: number) => GraafPunt;
  beweging: Beweging;
  /** thema › onderwerp, per onderwerp */
  onderwerpen: string[];
  bronnen: GraafBronnenResponse | null;
  vlaggen: string[];
  onClose: () => void;
  onKies: (id: string) => void;
  onVerband: (van: string, naar: string) => void;
  onOpenAlinea: (alineaId: string, citaat?: string | null) => void;
  onFocus?: () => void;
  focusAan?: boolean;
  onVraag?: (vraag: string) => void;
  /** Trendlaag aan: opent de trend van de trendlaag bij dit trendpunt. */
  onOpenTrend?: () => void;
}) {
  const laagAan = trendlaagAan();
  const [open, setOpen] = useState<Record<string, boolean>>({ boven: true, vraagt: true, gevraagd: true });
  const [sortering, setSortering] = useState<"sterkst" | "opkomend">("sterkst");
  const groepen = new Map<BuurSoort, Buur[]>();
  for (const b of buren) {
    const l = groepen.get(b.soort) ?? [];
    l.push(b);
    groepen.set(b.soort, l);
  }
  const badge = (tekst: string, kleur?: string, title?: string) => (
    <span key={tekst} title={title} style={{ fontSize: size.label, padding: "1px 8px", borderRadius: radius.pill, border: `1px solid ${kleur ?? color.border}`, color: kleur ?? color.body }}>
      {tekst}
    </span>
  );
  const knop = (aan = false) => ({
    cursor: "pointer", fontSize: size.label, padding: "4px 10px", borderRadius: radius.input, fontFamily: "inherit",
    border: `1px solid ${aan ? color.ink : color.border}`, background: aan ? color.ink : color.surface, color: aan ? color.surface : color.body,
  } as const);

  return (
    <div style={{ height: "100%", overflow: "auto" }}>
      <PanelHeader onClose={onClose}>
        <span style={{ fontSize: size.label, color: color.faint, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {punt.soort} · {punt.bronnen === 1 ? "1 bron" : `${punt.bronnen} bronnen`}
        </span>
      </PanelHeader>
      <div style={{ padding: "18px 24px 24px" }}>
        <h2 style={{ fontSize: size.panelTitle, fontWeight: weight.bold, color: color.ink, lineHeight: 1.3, margin: "0 0 6px", letterSpacing: "-0.02em" }}>{punt.naam}</h2>
        <div style={{ fontSize: size.label, color: color.faint, marginBottom: 10 }}>{onderwerpen.length ? onderwerpen.join(" · ") : "geen onderwerp"}</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
          {punt.impact != null && badge(`impact ${bandNaam(punt.impact, "impact")} (${punt.impact})`)}
          {punt.urgentie != null && badge(`urgentie ${bandNaam(punt.urgentie, "urgentie")} (${punt.urgentie})`)}
          {punt.moeite != null && badge(`moeite ${bandNaam(punt.moeite, "moeite")} (${punt.moeite})`)}
          {punt.eersteBewijs && badge(`bewijs ${punt.eersteBewijs} → ${punt.laatsteBewijs ?? "?"}`)}
          {beweging !== "onbekend" && badge(beweging, beweging === "opkomend" ? "#dc2626" : beweging === "stil" ? color.faint : "#2563eb")}
          {vlaggen.map((v) => badge(v, "#059669"))}
          {punt.quickWin && badge("quick win", "#059669")}
          {punt.uitgever && badge(`gedragen door ${UITGEVER_LABEL[punt.uitgever] ?? punt.uitgever}`)}
          {/* Methode v3: de bewijslaag van de trend. Zonder het veld staat er niets. */}
          {!laagAan && punt.bewijs?.laag === "bevestigd" && badge("bevestigd", color.accentGreen, bewijsTitel(punt.bewijs))}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
          {onFocus && <button type="button" style={knop(focusAan)} onClick={onFocus}>{focusAan ? "Focus uit" : "Focus: alleen dit en zijn buren"}</button>}
          {laagAan && onOpenTrend && punt.soort === "trend" && <button type="button" style={knop()} onClick={onOpenTrend}>Open de trend</button>}
          {onVraag && <button type="button" style={knop()} onClick={() => onVraag(`Wat zeggen de bronnen over “${punt.naam}”, en welke vaardigheden vraagt dit van ons onderwijs?`)}>Vraag de chat</button>}
          <span style={{ marginLeft: "auto", fontSize: size.label, color: color.faint }}>buren op</span>
          <button type="button" style={knop(sortering === "sterkst")} onClick={() => setSortering("sterkst")}>sterkst</button>
          <button type="button" style={knop(sortering === "opkomend")} onClick={() => setSortering("opkomend")}>opkomend</button>
        </div>

        {buren.length === 0 && <p style={{ fontSize: size.meta, color: color.faint }}>Geen buren in de data.</p>}
        {[...groepen.entries()].map(([soort, lijst]) => {
          const label = BUUR_LABEL[soort];
          const isOpen = open[soort] ?? false;
          const buiten = lijst.filter((b) => b.buiten).length;
          const gesorteerd = sortering === "opkomend"
            ? [...lijst].sort((a, b) => Number(b.opkomend ?? false) - Number(a.opkomend ?? false))
            : lijst;
          return (
            <div key={soort} style={{ borderTop: `1px solid ${color.lineFaint}`, padding: "8px 0" }}>
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [soort]: !isOpen }))}
                style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, width: "100%" }}
              >
                <i style={{ width: 8, height: 8, borderRadius: "50%", background: SOORT_KLEUR[soort], display: "block" }} />
                <span style={{ fontSize: size.meta, fontWeight: weight.medium, color: color.ink }}>{label.naam}</span>
                <span style={{ fontSize: size.label, color: color.faint }}>{label.waar}</span>
                <span style={{ marginLeft: "auto", fontSize: size.label, color: color.faint }}>{lijst.length}</span>
                <span style={{ fontSize: 10, color: color.faint, transform: isOpen ? "rotate(90deg)" : "none", display: "inline-block" }}>▶</span>
              </button>
              {isOpen && (
                <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0, display: "grid", gap: 2 }}>
                  {gesorteerd.slice(0, 10).map((b) => {
                    const q = puntVan(b.i);
                    return (
                      <li key={q.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, alignItems: "center", opacity: b.buiten ? 0.55 : 1 }}>
                        <button type="button" onClick={() => onKies(q.id)} style={{ all: "unset", cursor: "pointer", fontSize: size.meta, color: color.body, lineHeight: 1.35 }} title={b.buiten ? "buiten de afbakening of de filters" : undefined}>
                          {q.naam}{b.buiten ? <span style={{ color: color.faint }}> · buiten</span> : null}
                        </button>
                        <span style={{ fontSize: 10, color: color.faint, whiteSpace: "nowrap" }}>
                          {b.sterkte > 1 ? `${b.sterkte} bronnen` : b.zekerheid != null ? zekerheidBand(b.zekerheid) : ""}
                          {b.opkomend ? " · opkomend" : ""}
                        </span>
                        {soort !== "verwant" || true ? (
                          <button type="button" onClick={() => onVerband(punt.id, q.id)} style={{ ...knop(), padding: "1px 7px" }}>waarom</button>
                        ) : null}
                      </li>
                    );
                  })}
                  {lijst.length > 10 && <li style={{ fontSize: size.label, color: color.faint }}>en nog {lijst.length - 10}</li>}
                  {buiten > 0 && <li style={{ fontSize: size.label, color: color.faint }}>{buiten} hiervan {buiten === 1 ? "valt" : "vallen"} buiten de afbakening of de filters</li>}
                </ul>
              )}
            </div>
          );
        })}

        <div style={{ marginTop: 18 }}>
          <SectionLabel>Bronnen{bronnen ? ` · ${bronnen.bronnen.length}` : ""}</SectionLabel>
          {!bronnen && <p style={{ fontSize: size.meta, color: color.faint }}>bronnen laden…</p>}
          {bronnen?.bronnen.map((b) => (
            <div key={b.documentId} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "start", padding: "6px 0", borderTop: `1px solid ${color.lineFaint}` }}>
              <div>
                <div style={{ fontSize: size.meta, color: color.ink }}>{b.titel ?? b.uitgever ?? "bron"}</div>
                <div style={{ fontSize: size.label, color: color.faint }}>
                  {[b.uitgeverType ? UITGEVER_LABEL[b.uitgeverType] ?? b.uitgeverType : b.uitgever, b.datum, b.vermeldingen && b.vermeldingen > 1 ? `${b.vermeldingen} vermeldingen` : null].filter(Boolean).join(" · ")}
                </div>
                {/* Methode v3: wie het zegt en waarop. Zonder de velden niets. */}
                {(b.wie || b.onderbouwingLabel || b.onderbouwing || b.achterBetaalmuur) && (
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
                    <WiePil bron={b} />
                    <WaaropPil bron={b} />
                    <BetaalmuurPil bron={b} />
                  </div>
                )}
              </div>
              {b.alineaId
                ? <button type="button" style={{ ...knop(), padding: "2px 8px" }} onClick={() => onOpenAlinea(b.alineaId!, b.citaat)}>alinea</button>
                : <span style={{ fontSize: 10, color: color.faint }}>geen alinea</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
